import { cn } from "@/lib/utils";

const sizeMap = {
  sm: "w-6 h-[1.12rem]",
  md: "w-10 h-[2rem]",
  lg: "w-[3.25rem] h-[2.55rem]",
};

/** macOS-style recessed lines on the front flap (Big Sur+) */
const grooveLines = {
  sm: { a: "top-[62%]", b: "top-[72%]", inset: "left-[12%] right-[12%]" },
  md: { a: "top-[66%]", b: "top-[76%]", inset: "left-[13%] right-[13%]" },
  lg: { a: "top-[68%]", b: "top-[78%]", inset: "left-[14%] right-[14%]" },
};

type Props = {
  size?: keyof typeof sizeMap;
  className?: string;
  /** Slightly brighter (selected row) */
  highlight?: boolean;
};

/**
 * macOS Finder folder — reference blues (~#5cb7f2 → #3491d8), top tab, twin grooves, soft drop shadow.
 */
export function FolderIcon3D({ size = "md", className, highlight }: Props) {
  const g = grooveLines[size];

  const bodyGradient = highlight
    ? "linear-gradient(180deg, #6bc4f5 0%, #3d9ee0 55%, #3a9bdc 100%)"
    : "linear-gradient(180deg, #5cb7f2 0%, #41a0e8 45%, #3491d8 100%)";

  const tabGradient = highlight
    ? "linear-gradient(180deg, #82d2ff 0%, #5cb7f2 100%)"
    : "linear-gradient(180deg, #72c8fc 0%, #4dacf0 100%)";

  return (
    <div
      className={cn(
        "relative isolate select-none overflow-hidden rounded-[6px]",
        "[filter:drop-shadow(0_0.5px_1px_rgba(0,0,0,0.12))]",
        sizeMap[size],
        className
      )}
      aria-hidden
    >
      {/* Back tab — lighter strip, top-left */}
      <div
        className="absolute left-0 top-0 z-10 w-[46%] rounded-tl-[7px] rounded-tr-[5px]"
        style={{
          height: "30%",
          background: tabGradient,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.45), 0 0 0 0.5px rgba(36,120,190,0.2)",
        }}
      />

      {/* Main front face */}
      <div
        className="absolute bottom-0 left-0 right-0 z-20 rounded-[7px]"
        style={{
          top: "24%",
          background: bodyGradient,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(20,80,130,0.12)",
        }}
      />

      {/* Etched grooves — slightly darker than mid-tone blue */}
      <div
        className={cn(
          "pointer-events-none absolute z-30 h-px rounded-full bg-[#236a9e]/40 shadow-[0_1px_0_rgba(255,255,255,0.22)]",
          g.inset,
          g.a
        )}
      />
      <div
        className={cn(
          "pointer-events-none absolute z-30 h-px rounded-full bg-[#1f6594]/35 shadow-[0_1px_0_rgba(255,255,255,0.18)]",
          g.inset,
          g.b
        )}
      />
    </div>
  );
}
